import React, { useState, useEffect } from "react";

const SESSION_KEY = "potterton-session";
const PIN_SESSION_KEY = "potterton-pin-session";
// Fallback login for devices where Google's sign-in flow won't work at all
// (e.g. TV Bro on Google TV, which Google blocks as an insecure browser).
// Not a real secret — it ships in the client bundle like any REACT_APP_ var —
// so treat it as a shared-device convenience lock, not an access boundary.
const TV_PIN = process.env.REACT_APP_TV_PIN;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// Must exactly match the redirect_uri the auth.js function expects, which
// must in turn exactly match an "Authorized redirect URI" on the OAuth
// client in Google Cloud Console.
const REDIRECT_URI = "https://pottertonfamily.com/.netlify/functions/auth";

function buildGoogleAuthUrl() {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: "code",
    scope: "openid email profile",
    prompt: "select_account",
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

function decodeJwtPayload(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(atob(base64))));
  } catch {
    return null;
  }
}

function readStoredSession() {
  try {
    // PIN sessions don't expire on their own — this is a shared TV, not a
    // personal device, so there's no inactive account to time out.
    if (localStorage.getItem(PIN_SESSION_KEY) === "1") {
      return { isPin: true, name: "Kids View" };
    }
    const token = localStorage.getItem(SESSION_KEY);
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    if (!payload || !payload.exp || payload.exp * 1000 < Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return { token, email: payload.email, name: payload.name, picture: payload.picture, exp: payload.exp };
  } catch {
    return null;
  }
}

export default function AuthGate({ children }) {
  const [session, setSession] = useState(readStoredSession);
  const [error, setError] = useState("");
  const [pinMode, setPinMode] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState("");

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (TV_PIN && pinValue === TV_PIN) {
      localStorage.setItem(PIN_SESSION_KEY, "1");
      setSession({ isPin: true, name: "Kids View" });
      setPinError("");
    } else {
      setPinError("Incorrect PIN");
      setPinValue("");
    }
  };

  // Pick up the redirect back from Google's OAuth consent screen (see
  // auth.js): it lands here as #session_token=... or #auth_error=... on the
  // app's own URL. This replaces the old GIS rendered-button flow, which
  // Google's script silently refuses to render inside the standalone
  // home-screen app on iOS.
  useEffect(() => {
    if (session) return;
    const hash = window.location.hash;
    if (!hash.includes("session_token=") && !hash.includes("auth_error=")) return;
    const params = new URLSearchParams(hash.slice(1));
    const token = params.get("session_token");
    const authError = params.get("auth_error");
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (token) {
      const payload = decodeJwtPayload(token);
      if (payload) {
        localStorage.setItem(SESSION_KEY, token);
        setSession({ token, email: payload.email, name: payload.name, picture: payload.picture, exp: payload.exp });
        return;
      }
    }
    if (authError) {
      setError(authError === "not_authorized" ? "This Google account isn't authorized for Potterton HQ" : "Sign-in failed — try again.");
    }
  }, [session]);

  // Quietly extend the session in the background so an actively-used app
  // effectively never logs the person out — refresh once per day while open.
  useEffect(() => {
    if (!session || session.isPin) return;
    const refresh = async () => {
      try {
        const res = await fetch("/.netlify/functions/auth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "refresh", token: session.token }),
        });
        const data = await res.json();
        if (res.ok) {
          localStorage.setItem(SESSION_KEY, data.token);
          setSession(s => ({ ...s, token: data.token }));
        } else {
          localStorage.removeItem(SESSION_KEY);
          setSession(null);
        }
      } catch { /* offline — keep existing session, try again next time */ }
    };
    refresh();
    const interval = setInterval(refresh, 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [!!session]);

  const handleSignOut = () => {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(PIN_SESSION_KEY);
    setSession(null);
  };

  if (!session) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "var(--cream)", padding: "0 24px", fontFamily: "'Nunito', sans-serif" }}>
        <div style={{ fontSize: 44, marginBottom: 14 }}>🏡</div>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 600, color: "var(--charcoal)", marginBottom: 6 }}>Potterton HQ</div>
        <div style={{ fontSize: 14, color: "var(--muted)", marginBottom: 28, textAlign: "center" }}>Sign in with your family Google account</div>
        {!pinMode ? (
          <>
            <button
              onClick={() => { window.location.href = buildGoogleAuthUrl(); }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 24px", borderRadius: 999, border: "1px solid var(--border)", background: "var(--white)", fontSize: 14, fontWeight: 600, color: "var(--charcoal)", cursor: "pointer", fontFamily: "inherit" }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.94v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.95 10.71A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.71V4.96H.94A9 9 0 0 0 0 9c0 1.45.35 2.83.94 4.04l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .94 4.96l3.01 2.33C4.66 5.16 6.65 3.58 9 3.58z"/></svg>
              Sign in with Google
            </button>
            {TV_PIN && (
              <button
                onClick={() => { setPinError(""); setPinMode(true); }}
                style={{ marginTop: 16, background: "none", border: "none", color: "var(--muted)", fontSize: 13, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}
              >
                Use PIN instead
              </button>
            )}
          </>
        ) : (
          <form onSubmit={handlePinSubmit} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <input
              type="password"
              inputMode="numeric"
              autoFocus
              value={pinValue}
              onChange={(e) => setPinValue(e.target.value)}
              style={{ width: 160, padding: "11px 16px", borderRadius: 999, border: "1px solid var(--border)", background: "var(--white)", fontSize: 16, letterSpacing: 4, textAlign: "center", fontFamily: "inherit" }}
              placeholder="PIN"
            />
            <button
              type="submit"
              style={{ padding: "11px 24px", borderRadius: 999, border: "none", background: "var(--sage)", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
            >
              Continue
            </button>
            <button
              type="button"
              onClick={() => { setPinMode(false); setPinValue(""); setPinError(""); }}
              style={{ background: "none", border: "none", color: "var(--muted)", fontSize: 13, textDecoration: "underline", cursor: "pointer", fontFamily: "inherit" }}
            >
              Back to Google sign-in
            </button>
          </form>
        )}
        {(error || pinError) && <div style={{ marginTop: 16, fontSize: 13, color: "var(--rose)", textAlign: "center", maxWidth: 280 }}>{error || pinError}</div>}
      </div>
    );
  }

  return children({ user: session, signOut: handleSignOut });
}
